/**
 * What a failed retire should put on screen — the classifier, kept pure so it
 * can be tested without a store.
 *
 * Sibling of `pages/stations/objectActionOutcome.ts`, and it exists for the
 * same reason: an operator told "the product could not be retired" learns
 * nothing, and four different causes were all rendering as that one sentence.
 * "A failure must never look like an absence" applies to refusals too — a
 * refusal that does not say what refused it reads as the button being broken.
 *
 * The distinctions that matter, and why each is separate:
 *
 *  - **403** — the caller is not a master. Not a fault in the product; saying
 *    "cannot be retired" would send them looking for a booking that is not
 *    there.
 *  - **404** — the row is gone, so the list on screen is stale. The fix is a
 *    reload, which is a different instruction entirely.
 *  - **409 with blockers** — the world is not ready, and the server named
 *    which part. One line per reason, so unlinking the objects does not then
 *    reveal a live rental on the second attempt.
 *  - **5xx / unreachable** — our service, not their data. Retrying the same
 *    click is the wrong advice.
 *  - **anything else** — show the server's own message rather than inventing
 *    one (#2255, #2258).
 */
export interface RetireFailure {
    /** HTTP status, when there was a response at all. */
    status?: number;
    /**
     * `blockers` from the 409 body. Plain strings: the same classifier serves
     * the retire and the hard delete (#2636), each with its own reason set —
     * the caller's i18n prefix decides where `blocker_<reason>` resolves.
     */
    blockers?: string[];
    /** The server's own message, for the shapes we cannot classify. */
    message?: string;
    /** No response reached us (offline, DNS, CORS). */
    unreachable?: boolean;
}

export interface RetireOutcome {
    /**
     * Keys under `services.products.retire.*`, in the order they should be
     * listed. Empty only when `detail` carries the message instead.
     */
    reasonKeys: string[];
    /** The server's raw message — rendered only when no key applies. */
    detail?: string;
}

export const productRetireOutcome = (failure: RetireFailure): RetireOutcome => {
    if (failure.unreachable) return { reasonKeys: ['error_service'] };

    const status = failure.status ?? 0;
    if (status === 401 || status === 403) return { reasonKeys: ['error_forbidden'] };
    if (status >= 500) return { reasonKeys: ['error_service'] };
    if (status === 404) return { reasonKeys: ['error_stale'] };

    if (failure.blockers?.length) {
        return { reasonKeys: failure.blockers.map((b) => `blocker_${b}`) };
    }
    // A 409 that named no reason is still a refusal, not a crash — say so
    // rather than falling through to the raw message.
    if (status === 409) return { reasonKeys: ['error_blocked'] };

    return { reasonKeys: [], detail: failure.message };
};

/**
 * Pull the classifier's input out of an RTK Query rejection.
 *
 * `axiosBaseQuery` formats a rejection as `{ status, data }`, where `data` is
 * the parsed body when there was one and the axios message string when there
 * was not — so a non-object `data` is exactly the "no response we can read"
 * case. Same shape `utils/errorMessage.ts` reads.
 */
export const retireFailureFrom = (err: unknown): RetireFailure => {
    const e = err as { status?: unknown, data?: unknown } | null | undefined;
    const status = typeof e?.status === 'number' ? e.status : undefined;
    const data = e?.data;

    if (!data || typeof data !== 'object') {
        return { status, unreachable: status === undefined, message: typeof data === 'string' ? data : undefined };
    }
    const body = data as { blockers?: unknown, message?: unknown };
    return {
        status,
        unreachable: status === undefined,
        blockers: Array.isArray(body.blockers)
            ? body.blockers.filter((b): b is string => typeof b === 'string')
            : undefined,
        message: typeof body.message === 'string' ? body.message : undefined,
    };
};
